import { useState } from "react";
import { X } from "lucide-react";
import { MOODS } from "../lib/moods";
import { useI18n } from "../context/I18nContext";

export const MoodBadge = ({ mood, testid }) => {
  const { t } = useI18n();
  const m = MOODS.find((x) => x.key === mood);
  if (!m) return null;
  const Icon = m.icon;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs border" style={{ color: m.color, borderColor: `${m.color}55` }}>
      <Icon size={12} /> {t(`mood_${m.key}`)}
    </span>
  );
};

export const TagChip = ({ tag, onRemove, onClick, active, testid }) => (
  <span
    data-testid={testid}
    onClick={onClick}
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs border transition-colors ${onClick ? "cursor-pointer" : ""} ${
      active ? "bg-[#E5C158] text-[#060A12] border-[#E5C158]" : "border-white/10 text-[#C2C9DB] hover:border-[#E5C158]/40"
    }`}
  >
    #{tag}
    {onRemove && (
      <button type="button" data-testid={`${testid}-remove`} onClick={onRemove} className="hover:text-red-400"><X size={11} /></button>
    )}
  </span>
);

export const MoodTagEditor = ({ mood, tags, onMood, onTags, disabled }) => {
  const { t } = useI18n();
  const [input, setInput] = useState("");

  const add = () => {
    const v = input.trim().replace(/^#/, "").toLowerCase().slice(0, 30);
    if (v && !tags.includes(v) && tags.length < 10) onTags([...tags, v]);
    setInput("");
  };

  return (
    <div className="space-y-4">
      <div>
        <span className="label-gold block mb-3">{t("mood_label")}</span>
        <div className="flex flex-wrap gap-2" data-testid="mood-picker">
          {MOODS.map(({ key, icon: Icon, color }) => {
            const active = mood === key;
            return (
              <button
                key={key}
                type="button"
                disabled={disabled}
                data-testid={`mood-option-${key}`}
                onClick={() => onMood(active ? null : key)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors duration-200"
                style={active ? { background: `${color}22`, borderColor: color, color } : { borderColor: "rgba(255,255,255,0.1)", color: "#C2C9DB" }}
              >
                <Icon size={14} /> {t(`mood_${key}`)}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <span className="label-gold block mb-3">{t("tags_label")}</span>
        <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="tags-list">
          {tags.map((tag) => (
            <TagChip key={tag} tag={tag} testid={`tag-chip-${tag}`} onRemove={() => onTags(tags.filter((x) => x !== tag))} />
          ))}
        </div>
        <input
          data-testid="tags-input"
          className="input-navy"
          disabled={disabled || tags.length >= 10}
          placeholder={t("tags_ph")}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
          onBlur={add}
        />
      </div>
    </div>
  );
};
